/**
 * The Admin audit-log screen (f1.5): every state-changing request and every
 * authentication attempt, newest first, read-only.
 *
 * What it deliberately does NOT have, although the prototype drew them -- each
 * is a decision, not an omission, and must not be "restored" (design D2, D5,
 * D8):
 *
 * - no page numbers and no total -- both need a COUNT and an OFFSET over the
 *   largest table in the system; older entries append instead;
 * - no Export CSV -- an export streams the whole log past the system's access
 *   controls and is its own feature (f13.1 establishes export, in phase 13);
 * - no SERVER-side free-text search -- a substring scan of the largest table
 *   in the system is not a filter, it is an outage. The filter bar's search
 *   box narrows the rows already fetched, in the browser, and says so.
 *
 * Visibility of the screen comes from the server's navigation list. Hiding it
 * is presentation only: the SERVER refuses every non-Admin, and records the
 * refusal in this very log.
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpToLine,
  ChevronDown,
  Loader2,
  RotateCw,
  ScrollText,
  Shield,
  ShieldAlert,
} from 'lucide-react';

import { queryKeys } from '@/api/queryKeys';
import { useAuditActorsQuery, useAuditLogQuery } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';
import { nextSort, type SortState } from '@/ui/tableSort';

import { AuditFilterBar } from './AuditFilterBar';
import { AuditLogTable } from './AuditLogTable';
import {
  defaultFilters,
  filterProblem,
  isFiltered,
  toQuery,
  toggleActor,
  type ActorFilter,
  type AuditLogFilterState,
} from './auditFilters';
import { applySearch } from './auditSearch';
import { DEFAULT_SORT, sortEntries, type SortColumn } from './auditSort';
import { formatLocalShort, instantRange, zoneLabelFor } from './auditTime';

/** The navigation key the server grants to Platform Admin alone. */
const NAV_KEY = 'audit-log';

function PageHead() {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Audit log</h1>
        <div className="page-sub">
          Every state-changing request and every authentication attempt
        </div>
      </div>
      <div className="page-actions">
        <span className="badge pill-admin">
          <Shield aria-hidden="true" /> Admin only
        </span>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Audit log</h1>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="empty">
            <div className="e-icon e-icon-warning">
              <ShieldAlert aria-hidden="true" />
            </div>
            <h3>Platform Admins only</h3>
            <p>
              The audit log is available to Platform Admins. The server refuses anyone else,
              and records the refusal.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export function AuditLogPage() {
  const { canSee } = useSessionScope();
  // Presentation only: the navigation list is the server's decision, and the
  // server refuses the API calls regardless of what renders here.
  if (!canSee(NAV_KEY)) return <NoAccess />;
  return <AuditLogScreen />;
}

function AuditLogScreen() {
  const queryClient = useQueryClient();
  const defaults = useMemo(() => defaultFilters(), []);
  const [filters, setFilters] = useState<AuditLogFilterState>(defaults);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);

  const problem = filterProblem(filters);
  const query = useMemo(() => (problem ? null : toQuery(filters)), [filters, problem]);
  const zone = useMemo(() => zoneLabelFor(filters.timeZone), [filters.timeZone]);

  const list = useAuditLogQuery(query ?? {}, { enabled: query !== null });
  const pages = list.data?.pages ?? [];
  const loaded = useMemo(() => pages.flatMap((page) => page.entries), [pages]);
  /* Search narrows, THEN sort orders -- so the order the reader set survives
     a search, and the footer's "entries loaded" still counts what was fetched
     rather than what is currently on screen. */
  const matched = useMemo(
    () => applySearch(loaded, filters.text, filters.timeZone),
    [loaded, filters.text, filters.timeZone],
  );
  const entries = useMemo(() => sortEntries(matched, sort), [matched, sort]);
  const paged = pages.length > 1;

  /* The actor options follow the DATE RANGE only, never the actor filter
     itself: a list that shrank to the actors already chosen could not be
     used to choose a second one. */
  const actorRange = useMemo(() => {
    if (problem) return null;
    const range = instantRange(filters.fromDay, filters.toDay);
    return { occurred_from: range.from, occurred_to: range.to };
  }, [filters.fromDay, filters.toDay, problem]);

  const actors = useAuditActorsQuery(actorRange ?? {}, { enabled: actorRange !== null });

  const applyFilters = (next: AuditLogFilterState) => {
    setExpandedId(null);
    setFilters(next);
  };
  /* Clicking an actor in a row adds it to the selection, or takes it out
     again: the same gesture in both directions, and the same set the picker
     edits. */
  const filterByActor = (actor: ActorFilter) =>
    applyFilters({ ...filters, actors: toggleActor(filters.actors, actor) });
  const clearFilters = () => applyFilters(defaultFilters());

  const backToNewest = () => {
    if (!query) return;
    setExpandedId(null);
    // Drops every page but the first and refetches it -- the only way new
    // entries appear, because the list never refreshes behind the reader.
    void queryClient.resetQueries({ queryKey: queryKeys.auditLog.list(query) });
  };

  const initialLoading = query !== null && list.isPending;

  return (
    <>
      <PageHead />

      <AuditFilterBar
        filters={filters}
        problem={problem}
        canReset={isFiltered(filters, defaults)}
        actorOptions={actors.data?.actors ?? []}
        actorsLoading={actors.isPending && actorRange !== null}
        matchCount={matched.length}
        loadedCount={loaded.length}
        onChange={applyFilters}
        onReset={clearFilters}
      />

      {query === null ? (
        <div className="card">
          <div className="card-body">
            <div className="empty">
              <div className="e-icon">
                <ScrollText aria-hidden="true" />
              </div>
              <h3>Adjust the filters to load entries</h3>
              <p>{problem}</p>
            </div>
          </div>
        </div>
      ) : list.isError ? (
        <div className="card">
          <div className="card-body">
            <div className="empty">
              <div className="e-icon e-icon-warning">
                <AlertTriangle aria-hidden="true" />
              </div>
              <h3>The audit log could not be loaded</h3>
              <p>{list.error instanceof Error ? list.error.message : 'Unexpected error.'}</p>
              <p className="empty-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void list.refetch()}
                  disabled={list.isFetching}
                >
                  <RotateCw aria-hidden="true" /> Retry
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : !initialLoading && entries.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty">
              <div className="e-icon">
                <ScrollText aria-hidden="true" />
              </div>
              <h3>No entries match these filters</h3>
              <p>
                {loaded.length > 0
                  ? `None of the ${loaded.length} loaded ${
                      loaded.length === 1 ? 'entry' : 'entries'
                    } contain that text. Clear the search, or load older entries to search further back.`
                  : 'Widen the date range or remove a filter to see more of the log.'}
              </p>
              <p className="empty-actions">
                <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                  Clear filters
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card audit-card">
          <AuditLogTable
            entries={entries}
            timeZone={filters.timeZone}
            zone={zone}
            loading={initialLoading}
            expandedId={expandedId}
            sort={sort}
            onSort={(column) => setSort((current) => nextSort(current, column))}
            onToggle={(entryId) => setExpandedId((open) => (open === entryId ? null : entryId))}
            onFilterActor={filterByActor}
          />
        </div>
      )}

      {query !== null && loaded.length > 0 && !list.isError && (
        <div className="pagination audit-pagination">
          {/* The range is read from `loaded`, which is always newest-first as
              the server sent it, so re-sorting the table does not make the
              footer claim a different window. The sentence also states the
              one thing a reader could otherwise get wrong: the order applies
              to what has been loaded, not to the whole log. */}
          <span className="info">
            {loaded.length} {loaded.length === 1 ? 'entry' : 'entries'} loaded
            {' · '}
            {formatLocalShort(loaded[loaded.length - 1]?.occurred_at ?? '')} to{' '}
            {formatLocalShort(loaded[0]?.occurred_at ?? '')}
            {' · sorting orders the entries loaded'}
            {!list.hasNextPage && ' · end of the log for these filters'}
          </span>
          <div className="pg-btns">
            {paged && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={backToNewest}>
                <ArrowUpToLine aria-hidden="true" /> Back to newest
              </button>
            )}
            {list.hasNextPage && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
              >
                {list.isFetchingNextPage ? (
                  <Loader2 aria-hidden="true" className="spin" />
                ) : (
                  <ChevronDown aria-hidden="true" />
                )}
                Load older entries
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
